import { useMemo, type ReactNode } from 'react'
import { capitalizeWords, isNotEmpty } from '@terros/common'
import { Input, SegmentedControl, Stack, Switch } from '@mantine/core'
import { type ReportColumnVariantGroup, useTranslation } from '@front/model'
import { type CustomRenderColumnProps } from '../table/columns/ToggleColumns'
import { type TableColumnDef } from '../table/types'

const ALL_VARIANTS = '__all__'

export function useReportColumnVariantToggle(
  variantMap: Map<string, ReportColumnVariantGroup>
): (props: CustomRenderColumnProps<TableColumnDef>) => ReactNode {
  const { t } = useTranslation()
  const groupByColumn = useMemo(() => toVariantGroupByColumn(variantMap), [variantMap])

  return ({ column, defaultToggle, selectedColumns, setSelectedColumns }) => {
    const group = groupByColumn.get(column.id)
    if (!group || group.options.size < 2) return defaultToggle
    if (column.id !== group.representative) return null

    return (
      <ReportColumnVariantToggle
        column={column}
        group={group}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        t={t}
      />
    )
  }
}

function ReportColumnVariantToggle({
  column,
  group,
  setSelectedColumns,
  selectedColumns,
  t,
}: {
  column: TableColumnDef
  group: ReportColumnVariantGroup
  setSelectedColumns: (fn: (prev: string[]) => string[]) => void
  selectedColumns: string[]
  t: (s: string) => string
}): ReactNode {
  const selectedValues = group.allValues.filter((value) => selectedColumns.includes(value))
  const isAnySelected = isNotEmpty(selectedValues)
  const isAllSelected = selectedValues.length === group.allValues.length
  const value = isAllSelected ? ALL_VARIANTS : (selectedValues[0] ?? ALL_VARIANTS)

  const handleToggle = (checked: boolean): void => {
    setSelectedColumns((prev) => {
      const filtered = prev.filter((col) => !group.allValues.includes(col))
      if (!checked) return filtered
      return [...filtered, ...group.allValues]
    })
  }

  const handleVariantChange = (value: string): void => {
    setSelectedColumns((prev) => {
      const filtered = prev.filter((col) => !group.allValues.includes(col))
      if (value === ALL_VARIANTS) return [...filtered, ...group.allValues]
      return [...filtered, value]
    })
  }

  return (
    <Stack gap='xs' key={column.id}>
      <Switch checked={isAnySelected} label={column.label} onChange={(e) => handleToggle(e.currentTarget.checked)} />
      <Input.Description>{column.description}</Input.Description>
      {isAnySelected && (
        <SegmentedControl
          data={[
            { label: t('All'), value: ALL_VARIANTS },
            ...Array.from(group.options.entries()).map(([option, value]) => ({
              label: t(capitalizeWords(option)),
              value,
            })),
          ]}
          size='xs'
          value={value}
          onChange={handleVariantChange}
        />
      )}
    </Stack>
  )
}

function toVariantGroupByColumn(
  variantMap: Map<string, ReportColumnVariantGroup>
): Map<string, ReportColumnVariantGroup> {
  const groupByColumn = new Map<string, ReportColumnVariantGroup>()
  for (const group of variantMap.values()) {
    for (const value of group.allValues) {
      groupByColumn.set(value, group)
    }
  }

  return groupByColumn
}
